import { Injectable } from '@angular/core';
import { FlightItinerary } from '../interfaces';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { GlobalComponent } from '../../../shared/global';
import { RPurchase } from '../../../api/api-checkout/models/rq-checkout-save-booking';
import { BookingInsuranceService } from './booking-insurance.service';
import { Guid } from '../../../shared/utils';
import { DEVICES } from '../../../shared/constants';
import { TokenService } from '../../../api/api-nmviajes/services/token.service';
import { PlatformService } from '../../../services/platform/platform.service';
import { IUpSell } from '../../../api/api-checkout/models/rq-checkout-up-sell';
import { Passenger, Phone } from '../../../api/api-checkout/models/rq-checkout-booking';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly insuranceService: BookingInsuranceService,
    private readonly tokenService: TokenService,
    private readonly platformService: PlatformService
  ) {}

  sendFlightEngineBooking(itinerary: FlightItinerary, booking: any, counter: number) {
    const url = `${environment.urlApiMotorVuelos}/mv/save-booking`;

    const paymentType = Number.parseInt(booking.paymentMethod, 10);
    const creditCard = booking.creditCardForm;
    const contact = booking.contact;
    const invoice = booking.invoice;
    const insurance = this.insuranceService.getValue();

    const body = {
      groupId: itinerary.flight.id,
      segmentSelected: itinerary.segments,
      paymentType,
      card:
        paymentType === 0
          ? {
              cardNumber: creditCard.cardNumber.replace(/\s+/g, ''),
              expiration: creditCard.expiration,
              cvv: creditCard.cvv,
              cardOwner: creditCard.holderName,
              documentType: Number.parseInt(creditCard.documentType, 10),
              documentNumber: creditCard.documentNumber,
              numberQuotes: creditCard.installments,
              city: creditCard.billingCity,
              address: creditCard.billingAddress,
              email: contact.email,
              counter,
            }
          : {
              documentType: null,
              numberQuotes: creditCard.installments,
              counter,
            },
      contact: {
        idLoggin: contact.loginId,
        name: contact.name,
        lastName: contact.lastname,
        motherLastName: '',
        email: contact.email,
        address: '',
        isBilling: !!invoice && !!invoice.ruc,
        ruc: invoice?.ruc,
        subCode: 0,
        phones: [
          {
            type: '2',
            phoneNumber: contact.phoneNumber.replace(/\s+/g, ''),
            areaCode: '01',
            countryCode: contact.phoneCode,
          },
        ],
        company: {
          companyName: invoice?.companyName,
          companyAddress: invoice?.companyAddress,
          issuedToCompany: true,
          comment: '',
          documentType: 0,
          documentNumber: '',
        },
      },
      customer: {
        username: contact.email,
        firstName: contact.name,
        lastName: contact.lastname,
        email: contact.email,
        address: 'Lima - Peru',
      },
      passengers: booking.passengers.map((item: any) => {
        return {
          type: item.type,
          name: item.name,
          lastName: item.lastname,
          birthday: new Date(item.year, item.month - 1, item.day).toISOString(),
          nationality: item.nationality.value,
          documentType: Number.parseInt(item.documentType, 10),
          documentNumber: item.documentNumber,
          gender: item.gender,
          email: contact.email,
          phone: '',
        };
      }),
      idDispositivo: 3,
      includeSecure: !!insurance,
      secure: insurance,
      cuponPromoWeb: booking.promotionalCode ?? '',
      generateTicket: false,
      deviceSessionId: booking.deviceSessionId,
      comment: '',
    };

    const headers = new HttpHeaders().set(
      'Authorization',
      `Bearer ${GlobalComponent.tokenMotorVuelo}`
    );
    return this.httpClient.post<RPurchase>(url, body, { headers });
  }

  sendOpenPayBooking(itinerary: FlightItinerary, booking: any, upsell?: IUpSell) {
    const url = `${environment.nmConfigurations}/v1/api/Booking/TransactionAndPayment`;

    const authLinks = this.tokenService.webLinks;
    const bookingConditions = authLinks.find((link) => link.tipoEnlace === 'CR')?.link ?? '';
    const termsConditions = authLinks.find((link) => link.tipoEnlace === 'TC')?.link ?? '';
    const travelDocuments = authLinks.find((link) => link.tipoEnlace === 'DV')?.link ?? '';

    const creditCard = booking.creditCardForm;
    const contact = booking.contact;
    const insurance = this.insuranceService.getValue();
    const docTypes = ['DNI', 'CE', 'PAS'];

    const body = {
      trackingCode: Guid(),
      muteExceptions: environment.muteExceptions,
      caller: {
        company: GlobalComponent.appCompany,
        application: GlobalComponent.appApplication,
        deviceId: `${this.platformService.getDevice() ?? DEVICES.WEB}`,
        fromIP: '',
        fromBrowser: '',
      },
      parameter: {
        identifier: 'Pagos',
        typeOfOperation: 'Vuelos',
        typeOfSale: itinerary.isDomestic ? 'Nacional' : 'Internacional',
        signIn: { username: contact.email },
        customer: {
          city: creditCard.billingCity,
          documentType: docTypes[Number.parseInt(creditCard.documentType, 10)],
          documentNumber: creditCard.documentNumber,
          phone: contact.phoneNumber.replace(/\s+/g, ''),
          username: contact.email,
          firstName: contact.name,
          lastName: contact.lastname,
          address: '',
          email: contact.email,
        },
        holder: {
          firstname: creditCard.holderName,
          lastname: '',
          documentType: creditCard.documentType,
          documentNumber: creditCard.documentNumber,
          address: creditCard.billingAddress,
          city: creditCard.billingCity,
          email: contact.email,
        },
        flight: {
          sourceId: itinerary.flight.departure[0].originCity.code,
          destinationId: itinerary.flight.departure[0].destinationCity.code,
          airlineId: itinerary.flight.airline.code,
        },
        card: {
          number: creditCard.cardNumber.replace(/\s+/g, ''),
          securityCode: creditCard.cvv,
          expirationDate: creditCard.expiration,
        },
        paymentPlan: creditCard.installments || {
          payments: 0,
          payments_type: 'WITHOUT_INTEREST',
        },
        promotionalCode: booking.promotionalCode ?? '',
        application: GlobalComponent.appApplication,
        deviceSessionId: booking.deviceSessionId,
        groupId: itinerary.flight.id,
        mToken: GlobalComponent.tokenMotorVuelo,
        transactionId: GlobalComponent.transactionId,
        upSell: upsell?.name,
        hasInsurance: !!insurance?.totalPrice,
        segmentSelected: itinerary.segments,
        passengers: booking.passengers.map(
          (item: any) =>
            ({
              type: item.type,
              name: item.name,
              lastName: item.lastname,
              birthday: new Date(item.year, item.month - 1, item.day).toISOString(),
              nationality: item.nationality.value,
              documentType: Number.parseInt(item.documentType, 10),
              documentNumber: item.documentNumber,
              phone: '',
              gender: item.gender,
              isFrecuently: false,
              email: contact.email,
            }) as Passenger
        ),
        phones: [
          {
            type: '2',
            phoneNumber: contact.phoneNumber,
            areaCode: '01',
            countryCode: contact.phoneCode.slice(1),
          },
        ] as Phone[],
        exchangeRate: this.tokenService.exchangeRate,
        urlRedirect: environment.urlRedirectB,
        webLink: { termsConditions, bookingConditions, travelDocuments },
      },
    };
    return this.httpClient.post<any>(url, body);
  }
}
